import type {
  Campaign,
  CampaignTaskDomain,
  CampaignTaskType,
} from './model';

type PlainRecord = Record<string, unknown>;

function record(value: unknown): PlainRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as PlainRecord
    : null;
}

function dateOrEpoch(value: unknown): Date {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  return new Date(0);
}

function dateOrNull(value: unknown): Date | null {
  return value == null ? null : dateOrEpoch(value);
}

function enabledOption(value: unknown): { enabled: boolean } | undefined {
  const data = record(value);
  return data ? { enabled: data.enabled === true } : undefined;
}

function positiveInteger(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  const normalized = Math.trunc(value);
  return normalized >= 1 ? normalized : undefined;
}

function parseDomains(value: unknown): CampaignTaskDomain[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const domains = value.flatMap((entry) => {
    const data = record(entry);
    if (
      !data
      || typeof data.domainId !== 'string'
      || data.domainId.length === 0
      || typeof data.label !== 'string'
      || typeof data.targetCount !== 'number'
    ) return [];
    return [{
      domainId: data.domainId,
      label: data.label,
      targetCount: data.targetCount,
      ...(data.claimingPaused === true ? { claimingPaused: true } : {}),
    }];
  });
  return domains.length > 0 ? domains : undefined;
}

function parseTaskTypes(data: PlainRecord): CampaignTaskType[] {
  const rawEntries = Array.isArray(data.taskTypes)
      ? data.taskTypes
      : typeof data.taskTypeId === 'string' && data.taskTypeId.length > 0
      ? [{
          taskTypeId: data.taskTypeId,
          targetCount: typeof data.targetCount === 'number' ? data.targetCount : 0,
          domains: data.domains,
          llmReview: data.llmReview,
          llmValidator: data.llmValidator,
          claimingPaused: data.claimingPaused,
        }]
      : [];

  return rawEntries.flatMap((entry) => {
    const raw = record(entry);
    if (
      !raw
      || typeof raw.taskTypeId !== 'string'
      || raw.taskTypeId.length === 0
      || typeof raw.targetCount !== 'number'
    ) return [];
    const parsed: CampaignTaskType = {
      taskTypeId: raw.taskTypeId,
      targetCount: raw.targetCount,
    };
    const domains = parseDomains(raw.domains);
    if (domains) parsed.domains = domains;
    const llmReview = enabledOption(raw.llmReview);
    if (llmReview) parsed.llmReview = llmReview;
    const llmValidator = enabledOption(raw.llmValidator);
    if (llmValidator) parsed.llmValidator = llmValidator;
    if (raw.claimingPaused === true) parsed.claimingPaused = true;
    return [parsed];
  });
}

function parseAutoRelease(value: unknown): { enabled: boolean; days: number } | undefined {
  const data = record(value);
  if (!data) return undefined;
  const days = positiveInteger(data.days) ?? 7;
  return { enabled: data.enabled === true, days };
}

function parseReviewRelease(value: unknown): { enabled: boolean; hours: number } | undefined {
  const data = record(value);
  if (!data) return undefined;
  return { enabled: data.enabled === true, hours: Math.min(8760, positiveInteger(data.hours) ?? 4) };
}

function parseStatuses(value: unknown): Record<string, string> | undefined {
  const data = record(value);
  if (!data) return undefined;
  const entries = Object.entries(data).filter(([, status]) => typeof status === 'string' && status.length > 0);
  return entries.length > 0
    ? Object.fromEntries(entries) as Record<string, string>
    : undefined;
}

/** Parse a platform-normalized Firestore record into a campaign domain value. */
export function parseCampaign(id: string, raw: PlainRecord): Campaign {
  const statuses = parseStatuses(raw.alignedCampaignExpertStatuses);
  const activeExpertIds = Array.isArray(raw.activeExpertIds)
    ? raw.activeExpertIds.filter((value): value is string => typeof value === 'string')
    : [];
  return {
    id,
    name: typeof raw.name === 'string' ? raw.name : '',
    description: typeof raw.description === 'string' ? raw.description : '',
    createdBy: typeof raw.createdBy === 'string' ? raw.createdBy : '',
    createdByName: typeof raw.createdByName === 'string' ? raw.createdByName : '',
    createdAt: dateOrEpoch(raw.createdAt),
    archivedAt: dateOrNull(raw.archivedAt),
    paused: raw.paused === true,
    pauseAllowedTaskIds: Array.isArray(raw.pauseAllowedTaskIds)
      ? Array.from(new Set(raw.pauseAllowedTaskIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0).map((id) => id.trim())))
      : [],
    activeExpertIds,
    ...(statuses ? { alignedCampaignExpertStatuses: statuses } : {}),
    requireFirstApprovalBeforeMoreClaims: raw.requireFirstApprovalBeforeMoreClaims === true,
    maxTasksPerUser: positiveInteger(raw.maxTasksPerUser),
    maxTasksPerTrustedUser: positiveInteger(raw.maxTasksPerTrustedUser),
    maxClaimedTasksPerUser: positiveInteger(raw.maxClaimedTasksPerUser),
    submissionEmail: enabledOption(raw.submissionEmail),
    autoApprove: enabledOption(raw.autoApprove),
    autoReleaseStaleClaims: parseAutoRelease(raw.autoReleaseStaleClaims),
    autoReleaseStaleInProgress: parseAutoRelease(raw.autoReleaseStaleInProgress),
    autoReleaseStaleReviews: parseReviewRelease(raw.autoReleaseStaleReviews),
    escalationAdminId: typeof raw.escalationAdminId === 'string' && raw.escalationAdminId.trim()
      ? raw.escalationAdminId.trim()
      : null,
    taskTypes: parseTaskTypes(raw),
  };
}
